import FormLinkBanner from '@/components/FormLinkBanner'
import { lumi } from '@/lib/lumi'
import { useEffect, useRef, useState } from 'react'

// ─── Types ────────────────────────────────────────────────────────────────────

type CicloStatus =
  | 'aguardando_meditacao'
  | 'meditacao_enviada'
  | 'ciclo_concluido'
  | 'convidado_ciclo2'
  | 'processo_finalizado'

interface Formulario {
  _id: string
  name: string
  email: string
  createdAt: string
  updatedAt?: string
  meditacaoEnviadaEm?: string
  status?: CicloStatus
  observacoes?: string
  q1?: string; q2?: string; q3?: string; q4?: string; q5?: string; q6?: string
  q7?: string; q8?: string; q9?: string; q10?: string; q11?: string
  q12?: string; q13?: string; q14?: string; q15?: string
  q16?: string; q17?: string; q18?: string; q19?: string; q20?: string
  q21?: string; q22?: string; q23?: string; q24?: string; q25?: string
  q26?: string
  q27?: string[]
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: CicloStatus; label: string; color: string; bg: string; border: string }[] = [
  { value: 'aguardando_meditacao', label: 'Aguardando meditação',      color: '#9ca3af', bg: 'rgba(156,163,175,0.12)', border: 'rgba(156,163,175,0.35)' },
  { value: 'meditacao_enviada',    label: 'Meditação enviada',          color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.35)'  },
  { value: 'ciclo_concluido',      label: 'Ciclo concluído',            color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.35)' },
  { value: 'convidado_ciclo2',     label: 'Convidado para o Ciclo 2',   color: '#f472b6', bg: 'rgba(244,114,182,0.12)', border: 'rgba(244,114,182,0.35)' },
  { value: 'processo_finalizado',  label: 'Processo finalizado',        color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.35)'  },
]

const SECTIONS: { title: string; questions: { id: keyof Formulario; num: number; label: string }[] }[] = [
  {
    title: 'Bloco 1 — Programação Familiar',
    questions: [
      { id: 'q1',  num: 1,  label: 'Frase da mãe/figura materna sobre a vida' },
      { id: 'q2',  num: 2,  label: 'Frase do pai/figura paterna sobre a vida' },
      { id: 'q3',  num: 3,  label: 'Relacionamento dos pais — o que ensinou' },
      { id: 'q4',  num: 4,  label: 'O que era proibido sentir ou expressar' },
      { id: 'q5',  num: 5,  label: 'O que precisava fazer/ser para ser amado' },
      { id: 'q6',  num: 6,  label: 'Papel que ocupava na família' },
    ],
  },
  {
    title: 'Bloco 2 — Primeiras Impressões sobre Si Mesmo',
    questions: [
      { id: 'q7',  num: 7,  label: 'Momento de ser visto e valorizado' },
      { id: 'q8',  num: 8,  label: 'Situação que mudou algo em você' },
      { id: 'q9',  num: 9,  label: 'O que aprendeu sobre o que pode ser/ter/fazer' },
      { id: 'q10', num: 10, label: 'Crença da família sobre dinheiro' },
      { id: 'q11', num: 11, label: 'Espiritualidade/religião na casa' },
    ],
  },
  {
    title: 'Bloco 3 — O Corpo como Memória',
    questions: [
      { id: 'q12', num: 12, label: 'Onde sente mais tensão no corpo' },
      { id: 'q13', num: 13, label: 'Doença ou padrão físico recorrente' },
      { id: 'q14', num: 14, label: 'Relação com o corpo' },
      { id: 'q15', num: 15, label: 'O que acontece no corpo com medo/ansiedade' },
    ],
  },
  {
    title: 'Bloco 4 — Padrões que se Repetem',
    questions: [
      { id: 'q16', num: 16, label: 'Situação/conflito que se repete' },
      { id: 'q17', num: 17, label: 'Área com mais estagnação' },
      { id: 'q18', num: 18, label: 'O que mais evita sentir' },
      { id: 'q19', num: 19, label: 'O que acontece antes de conquistar algo' },
      { id: 'q20', num: 20, label: 'Pensamento sabotador recorrente' },
    ],
  },
  {
    title: 'Bloco 5 — O Momento Presente',
    questions: [
      { id: 'q21', num: 21, label: 'O que trouxe ao Protocolo' },
      { id: 'q22', num: 22, label: 'O que já tentou antes' },
      { id: 'q23', num: 23, label: 'Como seria a vida em um ano' },
      { id: 'q24', num: 24, label: 'Algo que nunca contou a ninguém' },
      { id: 'q25', num: 25, label: 'O que quer que Mônica saiba' },
    ],
  },
  {
    title: 'Bloco Final — Direcionamento',
    questions: [
      { id: 'q26', num: 26, label: 'Dor/padrão que mais quer resolver agora' },
    ],
  },
]

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso?: string, withTime = true) {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    }).format(new Date(iso))
  } catch { return iso }
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso)
  d.setDate(d.getDate() + days)
  return d.toISOString()
}

function getStatusMeta(status?: CicloStatus) {
  return STATUS_OPTIONS.find(s => s.value === (status || 'aguardando_meditacao')) ?? STATUS_OPTIONS[0]
}

// ─── Status Dropdown ─────────────────────────────────────────────────────────

function StatusDropdown({
  current,
  onChange,
}: {
  current?: CicloStatus
  onChange: (s: CicloStatus) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const meta = getStatusMeta(current)

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] tracking-[0.12em] uppercase font-body transition-all whitespace-nowrap"
        style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.border}` }}
      >
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: meta.color }} />
        {meta.label}
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 min-w-[190px] rounded-xl border border-white/10 shadow-2xl overflow-hidden"
             style={{ background: '#141c24' }}>
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={e => { e.stopPropagation(); onChange(opt.value); setOpen(false) }}
              className="w-full flex items-center gap-2 px-3 py-2 text-[12px] font-body hover:bg-white/5 transition-colors text-left"
              style={{ color: opt.color }}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: opt.color }} />
              {opt.label}
              {current === opt.value && (
                <svg className="w-3 h-3 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Date Input ───────────────────────────────────────────────────────────────

function DateField({
  label,
  value,
  onChange,
}: {
  label: string
  value?: string
  onChange: (iso: string | undefined) => void
}) {
  // Convert ISO to date-only string for input[type=date]
  const inputVal = value ? value.slice(0, 10) : ''

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)]">{label}</label>
      <input
        type="date"
        value={inputVal}
        onClick={e => e.stopPropagation()}
        onChange={e => {
          const v = e.target.value
          onChange(v ? new Date(v + 'T12:00:00.000Z').toISOString() : undefined)
        }}
        className="rounded-lg px-2.5 py-1.5 text-[12px] font-body numerals border border-white/10 bg-white/5 text-[var(--ink)] focus:outline-none focus:border-white/25 transition-colors"
        style={{ colorScheme: 'dark' }}
      />
    </div>
  )
}

// ─── Observações Inline ───────────────────────────────────────────────────────

function ObservacoesField({
  value,
  onChange,
}: {
  value?: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)]">Observações</label>
      <textarea
        value={value || ''}
        onClick={e => e.stopPropagation()}
        onChange={e => onChange(e.target.value)}
        rows={3}
        placeholder="Anotações sobre esta pessoa..."
        className="rounded-lg px-2.5 py-1.5 text-[12px] font-body border border-white/10 bg-white/5 text-[var(--ink)] focus:outline-none focus:border-white/25 transition-colors resize-none leading-relaxed placeholder:text-white/20"
      />
    </div>
  )
}

// ─── Form Card ────────────────────────────────────────────────────────────────

function FormCard({
  form,
  expanded,
  onToggle,
  onDelete,
  onUpdate,
}: {
  form: Formulario
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
  onUpdate: (patch: Partial<Formulario>) => void
}) {
  const statusMeta = getStatusMeta(form.status)
  const encerraEm = form.meditacaoEnviadaEm ? addDays(form.meditacaoEnviadaEm, 21) : null

  // Local state for editable fields (debounced save)
  const [localMed, setLocalMed] = useState(form.meditacaoEnviadaEm)
  const [localStatus, setLocalStatus] = useState<CicloStatus | undefined>(form.status)
  const [localObs, setLocalObs] = useState(form.observacoes || '')
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function scheduleSave(patch: Partial<Formulario>) {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => onUpdate(patch), 800)
  }

  function handleMedDate(iso: string | undefined) {
    setLocalMed(iso)
    scheduleSave({ meditacaoEnviadaEm: iso, updatedAt: new Date().toISOString() })
  }

  function handleStatus(s: CicloStatus) {
    setLocalStatus(s)
    onUpdate({ status: s, updatedAt: new Date().toISOString() })
  }

  function handleObs(v: string) {
    setLocalObs(v)
    scheduleSave({ observacoes: v, updatedAt: new Date().toISOString() })
  }

  return (
    <div
      className="rounded-2xl transition-all duration-200 overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.03)' }}
    >
      {/* ── Header row ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3 p-4 sm:p-5">
        <button onClick={onToggle} className="min-w-0 flex-1 text-left">
          <p className="font-display italic text-[17px] sm:text-[18px] text-[var(--ink)] leading-snug truncate">
            {form.name}
          </p>
          <p className="text-[12px] text-[var(--ink-soft)] font-body mt-0.5 truncate">{form.email}</p>
          <p className="text-[11px] text-[var(--ink-soft)] font-body opacity-60 mt-1 numerals">
            Formulário: {formatDate(form.createdAt)}
          </p>
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onToggle}
            className="px-3 py-1.5 rounded-lg text-[11px] tracking-[0.15em] uppercase font-body transition-all"
            style={{
              background: 'rgba(185,163,217,0.14)',
              color: '#D4C3EA',
              border: '1px solid rgba(185,163,217,0.35)',
            }}
          >
            {expanded ? 'Recolher' : 'Ver respostas'}
          </button>
          <button
            onClick={onDelete}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 hover:bg-red-500/15 active:scale-90 group"
            title="Excluir formulário"
            aria-label="Excluir formulário"
          >
            <svg className="w-4 h-4 text-[var(--ink-soft)] group-hover:text-red-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Management columns (always visible) ────────────────── */}
      <div
        className="mx-4 sm:mx-5 mb-4 rounded-xl px-3 py-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
        style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Data de envio da meditação */}
        <DateField
          label="Meditação enviada em"
          value={localMed}
          onChange={handleMedDate}
        />

        {/* Data de encerramento (calculada) */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)]">Encerramento (Dia 21)</span>
          {encerraEm ? (
            <span
              className="text-[12px] font-body numerals px-2.5 py-1.5 rounded-lg"
              style={{ color: 'var(--ink)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {formatDate(encerraEm, false)}
            </span>
          ) : (
            <span className="text-[12px] font-body text-[var(--ink-soft)] opacity-40 px-2.5 py-1.5">—</span>
          )}
        </div>

        {/* Status */}
        <div className="flex flex-col gap-1">
          <span className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)]">Status</span>
          <StatusDropdown current={localStatus} onChange={handleStatus} />
        </div>


      </div>

      {/* ── Observações (always visible) ───────────────────────── */}
      <div className="mx-4 sm:mx-5 mb-4" onClick={e => e.stopPropagation()}>
        <ObservacoesField value={localObs} onChange={handleObs} />
      </div>

      {/* ── Expanded: full answers ──────────────────────────────── */}
      {expanded && (
        <div className="px-4 sm:px-5 pb-5 space-y-6 border-t border-white/8 pt-5">

          {/* Dados pessoais */}
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-3" style={{ color: '#D4C3EA' }}>
              Dados Pessoais
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { label: 'Nome completo', value: form.name },
                { label: 'Email', value: form.email },
              ].map(row => (
                <div key={row.label} className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">{row.label}</p>
                  <p className="text-[13px] font-body text-[var(--ink)]">{row.value || '—'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Resumo do ciclo */}
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-3" style={{ color: '#D4C3EA' }}>
              Resumo do Ciclo
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">Data de envio do formulário</p>
                <p className="text-[13px] font-body numerals text-[var(--ink)]">{formatDate(form.createdAt)}</p>
              </div>
              <div className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">Data de envio da meditação</p>
                <p className="text-[13px] font-body numerals text-[var(--ink)]">{formatDate(localMed, false) !== '—' ? formatDate(localMed, false) : '—'}</p>
              </div>
              <div className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">Data de encerramento (Dia 21)</p>
                <p className="text-[13px] font-body numerals text-[var(--ink)]">{encerraEm ? formatDate(encerraEm, false) : '—'}</p>
              </div>
              <div className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">Status atual</p>
                <span
                  className="inline-flex items-center gap-1.5 text-[12px] font-body px-2 py-0.5 rounded-md"
                  style={{ color: statusMeta.color, background: statusMeta.bg, border: `1px solid ${statusMeta.border}` }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusMeta.color }} />
                  {statusMeta.label}
                </span>
              </div>
              {localObs && (
                <div className="rounded-lg px-3 py-2.5 sm:col-span-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">Observações</p>
                  <p className="text-[13px] font-body text-[var(--ink)] whitespace-pre-wrap leading-relaxed">{localObs}</p>
                </div>
              )}
            </div>
          </div>

          {/* Respostas por bloco */}
          {SECTIONS.map(sec => (
            <div key={sec.title}>
              <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-3" style={{ color: '#D4C3EA' }}>
                {sec.title}
              </p>
              <div className="space-y-3">
                {sec.questions.map(q => (
                  <div key={q.id as string} className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-[11px] font-body text-[var(--ink-soft)] mb-1">
                      <span className="numerals">{String(q.num).padStart(2, '0')}</span>
                      {' · '}
                      {q.label}
                    </p>
                    <p className="text-[13px] font-body text-[var(--ink)] whitespace-pre-wrap leading-relaxed">
                      {((form[q.id] as string) || '').trim() || <span className="italic opacity-50">(sem resposta)</span>}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Q27 — áreas de urgência */}
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-3" style={{ color: '#D4C3EA' }}>
              Bloco Final — Direcionamento (cont.)
            </p>
            <div className="rounded-lg px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p className="text-[11px] font-body text-[var(--ink-soft)] mb-2">
                <span className="numerals">27</span> · Áreas de urgência (até 2)
              </p>
              <div className="flex flex-wrap gap-2">
                {(form.q27 && form.q27.length > 0) ? form.q27.map(area => (
                  <span
                    key={area}
                    className="text-[12px] font-body px-2.5 py-1 rounded-md"
                    style={{
                      background: 'rgba(185,163,217,0.14)',
                      color: '#D4C3EA',
                      border: '1px solid rgba(185,163,217,0.35)',
                    }}
                  >
                    {area}
                  </span>
                )) : (
                  <span className="italic text-[13px] text-[var(--ink-soft)] opacity-50">(sem seleção)</span>
                )}
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function AdminFormularios() {
  const [forms, setForms]           = useState<Formulario[]>([])
  const [loading, setLoading]       = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Formulario | null>(null)

  useEffect(() => {
    fetchForms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function fetchForms() {
    setLoading(true)
    try {
      const { list } = await lumi.entities.formulario_protocolo.list({
        sort: { createdAt: -1 },
        limit: 500,
      })
      setForms(list as Formulario[])
    } catch (e) {
      console.error('Erro ao buscar formulários:', e)
    } finally {
      setLoading(false)
    }
  }

  async function handleUpdate(id: string, patch: Partial<Formulario>) {
    setForms(prev => prev.map(f => f._id === id ? { ...f, ...patch } : f))
    try {
      await lumi.entities.formulario_protocolo.update(id, patch)
    } catch (e) {
      console.error('Erro ao atualizar formulário:', e)
      fetchForms()
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return
    const id = deleteTarget._id
    setDeleteTarget(null)
    setForms(prev => prev.filter(f => f._id !== id))
    try {
      await lumi.entities.formulario_protocolo.delete(id)
    } catch (e) {
      console.error('Erro ao excluir formulário:', e)
      fetchForms()
    }
  }

  const aguardandoCount    = forms.filter(f => (f.status || 'aguardando_meditacao') === 'aguardando_meditacao').length
  const concluidoCount     = forms.filter(f => f.status === 'ciclo_concluido').length
  const finalizadoCount    = forms.filter(f => f.status === 'processo_finalizado').length

  return (
    <>
      <FormLinkBanner link="https://jornada.reacendaluz.com.br/formulario" />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-[#D4C3EA] border-t-transparent animate-spin" />
            <p className="text-[13px] font-body text-[var(--ink-soft)]">Carregando formulários…</p>
          </div>
        </div>
      ) : forms.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-[15px] font-display italic text-[var(--ink-soft)] mb-2">
            Nenhum formulário recebido ainda
          </p>
          <p className="text-[12px] font-body text-[var(--ink-soft)] opacity-60">
            Os formulários de investigação profunda aparecerão aqui após o envio pelo link acima.
          </p>
        </div>
      ) : (
        <>
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl p-3 sm:p-4 border border-white/8" style={{ background: 'rgba(255,255,255,0.025)' }}>
          <p className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1">Total</p>
          <p className="numerals text-[26px] sm:text-[30px] font-display leading-none" style={{ color: '#D4C3EA' }}>{forms.length}</p>
        </div>
        <div className="rounded-xl p-3 sm:p-4 border border-white/8" style={{ background: 'rgba(255,255,255,0.025)' }}>
          <p className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1">Aguardando</p>
          <p className="numerals text-[26px] sm:text-[30px] font-display leading-none" style={{ color: '#9ca3af' }}>{aguardandoCount}</p>
        </div>
        <div className="rounded-xl p-3 sm:p-4 border border-white/8" style={{ background: 'rgba(255,255,255,0.025)' }}>
          <p className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1">Concluídos</p>
          <p className="numerals text-[26px] sm:text-[30px] font-display leading-none" style={{ color: '#a78bfa' }}>{concluidoCount}</p>
        </div>
        <div className="rounded-xl p-3 sm:p-4 border border-white/8" style={{ background: 'rgba(255,255,255,0.025)' }}>
          <p className="text-[10px] tracking-[0.2em] uppercase font-body text-[var(--ink-soft)] mb-1">Finalizados</p>
          <p className="numerals text-[26px] sm:text-[30px] font-display leading-none" style={{ color: '#34d399' }}>{finalizadoCount}</p>
        </div>
      </div>

      <div className="space-y-3">
        {forms.map(f => (
          <FormCard
            key={f._id}
            form={f}
            expanded={expandedId === f._id}
            onToggle={() => setExpandedId(prev => prev === f._id ? null : f._id)}
            onDelete={() => setDeleteTarget(f)}
            onUpdate={patch => handleUpdate(f._id, patch)}
          />
        ))}
      </div>

      <p className="text-center text-[11px] font-body text-[var(--ink-soft)] opacity-50 mt-6 numerals">
        {forms.length} {forms.length === 1 ? 'formulário' : 'formulários'} exibido{forms.length === 1 ? '' : 's'}
      </p>
        </>
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5"
             style={{ background: 'rgba(10,15,20,0.85)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6 shadow-2xl border border-white/10"
               style={{ background: '#141c24' }}>
            <h3 className="font-display italic text-[20px] text-[var(--ink)] text-center mb-2">
              Excluir formulário
            </h3>
            <p className="text-[13px] text-[var(--ink-soft)] font-body text-center leading-relaxed mb-6">
              Deseja realmente excluir o formulário de{' '}
              <span className="text-[var(--ink)] font-medium">{deleteTarget.name}</span>?
              Essa ação é permanente.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-xl font-body text-[13px] tracking-[0.1em] uppercase active:scale-[0.97]"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--ink-soft)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 py-3 rounded-xl font-body text-[13px] tracking-[0.1em] uppercase active:scale-[0.97]"
                style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
